import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { TransportMap } from '@/components/transport/TransportMap';
import { TransporterCard } from '@/components/transport/TransporterCard';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Search, MapPin, Truck } from 'lucide-react';
import { Helmet } from 'react-helmet-async';

const STATE_NAMES: Record<string, string> = {
  SC: 'South Carolina',
  NC: 'North Carolina',
  GA: 'Georgia',
  FL: 'Florida',
  VA: 'Virginia',
  TN: 'Tennessee',
  AL: 'Alabama',
};

export default function TransportDirectory() {
  const { stateCode = 'SC' } = useParams<{ stateCode: string }>();
  const upperStateCode = stateCode.toUpperCase();
  const stateName = STATE_NAMES[upperStateCode] || upperStateCode;
  
  const [selectedId, setSelectedId] = useState<string | undefined>();
  const [searchTerm, setSearchTerm] = useState('');
  const [speciesFilter, setSpeciesFilter] = useState<string | null>(null);
  const [mapboxToken, setMapboxToken] = useState<string>('');

  // Fetch Mapbox token from edge function
  useEffect(() => {
    const fetchToken = async () => {
      try {
        const { data, error } = await supabase.functions.invoke('geocode-address', {
          body: { action: 'get-token' }
        });
        if (data?.token) {
          setMapboxToken(data.token);
        }
      } catch (err) {
        console.error('Failed to fetch map token:', err);
      }
    };
    fetchToken();
  }, []);

  const { data: transporters = [], isLoading } = useQuery({
    queryKey: ['transporters', upperStateCode],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('transporters')
        .select('*')
        .eq('state_code', upperStateCode)
        .eq('is_active', true)
        .order('name');
      
      if (error) throw error;
      return data || [];
    },
  });

  // Get unique species for filter
  const allSpecies = React.useMemo(() => {
    const speciesSet = new Set<string>();
    transporters.forEach(t => {
      t.species_handled?.forEach((s: string) => speciesSet.add(s));
    });
    return Array.from(speciesSet).sort();
  }, [transporters]);

  // Filter transporters
  const filteredTransporters = React.useMemo(() => {
    return transporters.filter(t => {
      const matchesSearch = !searchTerm || 
        t.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        t.city.toLowerCase().includes(searchTerm.toLowerCase()) ||
        t.bio?.toLowerCase().includes(searchTerm.toLowerCase());
      
      const matchesSpecies = !speciesFilter || 
        t.species_handled?.includes(speciesFilter);
      
      return matchesSearch && matchesSpecies;
    });
  }, [transporters, searchTerm, speciesFilter]);

  return (
    <div className="min-h-screen bg-background">
      <Helmet>
        <title>Animal Transporters in {stateName} | Transport Directory</title>
        <meta name="description" content={`Find verified volunteer animal transporters in ${stateName}.`} />
      </Helmet>
      {/* Header */}
      <header className="border-b bg-card">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center gap-3">
            <Truck className="h-6 w-6 text-primary" />
            <div>
              <h1 className="text-xl font-semibold">Animal Transport Directory</h1>
              <p className="text-sm text-muted-foreground flex items-center gap-1">
                <MapPin className="h-3 w-3" />
                {stateName}
              </p>
            </div>
          </div>
        </div>
      </header>

      <div className="flex flex-col lg:flex-row h-[calc(100vh-73px)]">
        {/* Sidebar */}
        <div className="w-full lg:w-96 border-r bg-card flex flex-col">
          {/* Search and filters */}
          <div className="p-4 border-b space-y-3">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search transporters..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-9"
              />
            </div>
            
            {allSpecies.length > 0 && (
              <div className="flex flex-wrap gap-1.5">
                <Badge
                  variant={speciesFilter === null ? "default" : "outline"}
                  className="cursor-pointer text-xs"
                  onClick={() => setSpeciesFilter(null)}
                >
                  All
                </Badge>
                {allSpecies.slice(0, 6).map(species => (
                  <Badge
                    key={species}
                    variant={speciesFilter === species ? "default" : "outline"}
                    className="cursor-pointer text-xs"
                    onClick={() => setSpeciesFilter(species === speciesFilter ? null : species)}
                  >
                    {species}
                  </Badge>
                ))}
              </div>
            )}
          </div>

          {/* Transporter list */}
          <ScrollArea className="flex-1">
            <div className="p-4 space-y-3">
              {isLoading ? (
                <div className="text-center py-8 text-muted-foreground">
                  Loading transporters...
                </div>
              ) : filteredTransporters.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <Truck className="h-12 w-12 mx-auto mb-3 opacity-50" />
                  <p>No transporters found</p>
                  <p className="text-sm mt-1">Try adjusting your search or filters</p>
                </div>
              ) : (
                filteredTransporters.map(transporter => (
                  <TransporterCard
                    key={transporter.id}
                    transporter={transporter}
                    isSelected={selectedId === transporter.id}
                    onClick={() => setSelectedId(transporter.id)}
                  />
                ))
              )}
            </div>
          </ScrollArea>

          <div className="p-3 border-t text-xs text-muted-foreground text-center">
            {filteredTransporters.length} transporter{filteredTransporters.length !== 1 ? 's' : ''} available
          </div>
        </div>

        {/* Map */}
        <div className="flex-1 relative">
          <TransportMap
            transporters={filteredTransporters}
            selectedId={selectedId}
            onMarkerClick={setSelectedId}
            mapboxToken={mapboxToken}
            stateCode={upperStateCode}
          />
        </div>
      </div>
    </div>
  );
}
